import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Shield, MapPin, BarChart3, Cookie, ChevronDown, ChevronUp } from 'lucide-react';
import { useConsent, type ConsentPreferences } from '@/hooks/useConsent';
import { Link } from 'react-router-dom';

export const CookieConsent = () => {
  const { hasConsented, acceptAll, savePreferences } = useConsent();
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [location, setLocation] = useState(true);

  if (hasConsented) return null;

  const handleSaveCustom = () => {
    const prefs: ConsentPreferences = {
      functional: true,
      analytics,
      location,
    };
    savePreferences(prefs);
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-[9999] flex justify-center p-3 sm:p-4 pointer-events-none">
      <div className="w-full max-w-2xl pointer-events-auto bg-card/95 border border-border rounded-2xl shadow-lg overflow-hidden backdrop-saturate-150">
        <div className="p-4 sm:p-5">
          <div className="flex items-start gap-3 mb-3">
            <div className="p-2 rounded-xl bg-primary/10 shrink-0">
              <Cookie className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-bold text-foreground">
                Jouw privacy, jouw keuze
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed mt-1">
                Wij gebruiken cookies en je geschatte locatie (op basis van je IP-adres) om je de beste rijscholen en opleiders in jouw omgeving te tonen.
              </p>
            </div>
          </div>

          <button
            onClick={() => setShowDetails(!showDetails)}
            className="w-full flex items-center justify-between px-1 py-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
          >
            <span>Voorkeuren aanpassen</span>
            {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>

          {showDetails && (
            <div className="pt-2 pb-3 space-y-3">
              <div className="flex items-center justify-between p-3 rounded-xl bg-muted/50">
                <div className="flex items-center gap-3">
                  <Shield className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Functioneel</p>
                    <p className="text-xs text-muted-foreground">Noodzakelijk voor de werking</p>
                  </div>
                </div>
                <Switch checked disabled className="opacity-60" />
              </div>

              <div className="flex items-center justify-between p-3 rounded-xl bg-muted/50">
                <div className="flex items-center gap-3">
                  <BarChart3 className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Analytisch</p>
                    <p className="text-xs text-muted-foreground">Helpt ons de site te verbeteren</p>
                  </div>
                </div>
                <Switch checked={analytics} onCheckedChange={setAnalytics} />
              </div>

              <div className="flex items-center justify-between p-3 rounded-xl bg-muted/50">
                <div className="flex items-center gap-3">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Locatie (omgeving)</p>
                    <p className="text-xs text-muted-foreground">Toont rijscholen bij jou in de buurt</p>
                  </div>
                </div>
                <Switch checked={location} onCheckedChange={setLocation} />
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-muted-foreground order-2 sm:order-1">
              Lees onze{' '}
              <Link to="/privacy" className="underline hover:text-foreground">
                privacyverklaring
              </Link>{' '}
              voor meer informatie.
            </p>

            <div className="order-1 sm:order-2 flex w-full sm:w-auto flex-col gap-2 sm:flex-row">
              {showDetails && (
                <Button
                  onClick={handleSaveCustom}
                  variant="outline"
                  className="w-full sm:w-auto"
                  size="lg"
                >
                  Bewaar keuze
                </Button>
              )}
              <Button
                onClick={acceptAll}
                className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"
                size="lg"
              >
                Accepteren
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
